import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { fetchVisitor, getOwnVisitorColor, getOwnVisitorId, setOwnVisitorColor } from '../api/visitorsApi';

const VisitorAccentColorContext = createContext(null);

/**
 * Provides the visitor's accent color to the whole app via a single shared
 * source. This replaces the previous per-component hook that fetched the full
 * visitor list on every route change (once per consumer), which caused several
 * duplicate /visitors requests. Now we fetch just the user's own record once.
 */
export function VisitorAccentColorProvider({ children }) {
  const [color, setColor] = useState(() => getOwnVisitorColor());
  const fetchedRef = useRef(false);

  // Keep in sync when the color is changed elsewhere (e.g. onboarding).
  useEffect(() => {
    const onColorChange = () => setColor(getOwnVisitorColor());
    window.addEventListener('visitor-color-change', onColorChange);
    return () => window.removeEventListener('visitor-color-change', onColorChange);
  }, []);

  // Fetch the user's own color once per session (not on every navigation).
  useEffect(() => {
    if (fetchedRef.current) return;
    const ownId = getOwnVisitorId();
    if (!ownId) return;
    fetchedRef.current = true;
    fetchVisitor(ownId)
      .then((own) => {
        if (own?.color_value) {
          setOwnVisitorColor(own.color_value);
          setColor(own.color_value);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <VisitorAccentColorContext.Provider value={color}>
      {children}
    </VisitorAccentColorContext.Provider>
  );
}

export default function useVisitorAccentColor() {
  const ctx = useContext(VisitorAccentColorContext);
  // Fallback for any consumer rendered outside the provider.
  return ctx ?? getOwnVisitorColor();
}
